"use strict";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
Object.defineProperty(exports, "__esModule", { value: true });
exports.createCookieStorageAdapterFromNextServerContext = exports.DATE_IN_THE_PAST = void 0;
const server_js_1 = require("next/server.js");
const internals_1 = require("aws-amplify/adapter-core/internals");
const utils_1 = require("../auth/utils");
const cookie_1 = require("./cookie");
exports.DATE_IN_THE_PAST = new Date(0);
const createCookieStorageAdapterFromNextServerContext = async (context, ignoreNonServerSideCookies = false) => {
    const { request: req, response: res } = context;
    // When the server context is from `getServerSideProps`, the `req` is an instance
    // of IncomingMessage, and the `res` is an instance of ServerResponse.
    // We cannot import these two classes here from `http` as it breaks in Next
    // Edge Runtime. Hence, we check the methods that we need to use for creating
    // cookie adapter.
    if (req &&
        res &&
        Object.prototype.toString.call(req.cookies) === '[object Object]' &&
        typeof res.setHeader === 'function') {
        return createCookieStorageAdapterFromGetServerSidePropsContext(req, res, ignoreNonServerSideCookies);
    }
    const { request, response } = context;
    // When the server context is from `middleware`, the `request` is an instance
    // of `NextRequest`.
    // When the server context is from a route handler, the `request` is an `Proxy`
    // wrapped `Request`.
    // The `NextRequest` and the `Proxy` are sharing the same interface by Next
    // implementation. So we don't need to detect the difference.
    if (request && response) {
        if (response instanceof server_js_1.NextResponse) {
            return createCookieStorageAdapterFromNextRequestAndNextResponse(request, response, ignoreNonServerSideCookies);
        }
        else {
            return createCookieStorageAdapterFromNextRequestAndHttpResponse(request, response, ignoreNonServerSideCookies);
        }
    }
    const { cookies } = context;
    if (typeof cookies === 'function') {
        return createCookieStorageAdapterFromNextCookies(cookies, ignoreNonServerSideCookies);
    }
    // This should not happen normally.
    throw new internals_1.AmplifyServerContextError({
        message: 'Attempted to create cookie storage adapter from an unsupported Next.js server context.',
    });
};
exports.createCookieStorageAdapterFromNextServerContext = createCookieStorageAdapterFromNextServerContext;
const createCookieStorageAdapterFromNextRequestAndNextResponse = (request, response, ignoreNonServerSideCookies) => {
    const readonlyCookieStore = request.cookies;
    const mutableCookieStore = response.cookies;
    return {
        get(name) {
            return readonlyCookieStore.get((0, cookie_1.ensureEncodedForJSCookie)(name));
        },
        getAll: readonlyCookieStore.getAll.bind(readonlyCookieStore),
        set(name, value, options) {
            if (shouldIgnoreCookie(ignoreNonServerSideCookies, name)) {
                return;
            }
            mutableCookieStore.set((0, cookie_1.ensureEncodedForJSCookie)(name), value, options);
        },
        delete(name) {
            if (shouldIgnoreCookie(ignoreNonServerSideCookies, name)) {
                return;
            }
            mutableCookieStore.delete((0, cookie_1.ensureEncodedForJSCookie)(name));
        },
    };
};
const createCookieStorageAdapterFromNextRequestAndHttpResponse = (request, response, ignoreNonServerSideCookies) => {
    const readonlyCookieStore = request.cookies;
    const mutableCookieStore = createMutableCookieStoreFromHeaders(response.headers, ignoreNonServerSideCookies);
    return {
        get(name) {
            return readonlyCookieStore.get((0, cookie_1.ensureEncodedForJSCookie)(name));
        },
        getAll: readonlyCookieStore.getAll.bind(readonlyCookieStore),
        ...mutableCookieStore,
    };
};
const createCookieStorageAdapterFromNextCookies = async (cookies, ignoreNonServerSideCookies) => {
    const cookieStore = await cookies();
    // When Next cookies() is called in a server component, it returns a readonly
    // cookie store. Hence calling set and delete throws an error. However,
    // cookies() returns a mutable cookie store when called in a server action.
    // We have no way to detect which one is returned, so we try to call set and delete
    // and safely ignore the error if it is thrown.
    const setFunc = (name, value, options) => {
        if (shouldIgnoreCookie(ignoreNonServerSideCookies, name)) {
            return;
        }
        try {
            cookieStore.set((0, cookie_1.ensureEncodedForJSCookie)(name), value, options);
        }
        catch {
            // no-op
        }
    };
    const deleteFunc = name => {
        if (shouldIgnoreCookie(ignoreNonServerSideCookies, name)) {
            return;
        }
        try {
            cookieStore.delete((0, cookie_1.ensureEncodedForJSCookie)(name));
        }
        catch {
            // no-op
        }
    };
    return {
        get(name) {
            return cookieStore.get((0, cookie_1.ensureEncodedForJSCookie)(name));
        },
        getAll: cookieStore.getAll.bind(cookieStore),
        set: setFunc,
        delete: deleteFunc,
    };
};
const createCookieStorageAdapterFromGetServerSidePropsContext = (request, response, ignoreNonServerSideCookies) => {
    const cookiesMap = { ...request.cookies };
    const allCookies = Object.entries(cookiesMap).map(([name, value]) => ({
        name,
        value,
    }));
    return {
        get(name) {
            const value = cookiesMap[(0, cookie_1.ensureEncodedForJSCookie)(name)];
            return value
                ? {
                    name,
                    value,
                }
                : undefined;
        },
        getAll() {
            return allCookies;
        },
        set(name, value, options) {
            if (shouldIgnoreCookie(ignoreNonServerSideCookies, name)) {
                return;
            }
            const encodedName = (0, cookie_1.ensureEncodedForJSCookie)(name);
            const existingValues = getExistingSetCookieValues(response.getHeader('Set-Cookie'));
            // if the cookies have already been set, we don't need to set them again.
            if (existingValues.findIndex(cookieValue => cookieValue.startsWith(`${encodedName}=`) &&
                !cookieValue.startsWith(`${encodedName}=;`)) > -1) {
                return;
            }
            response.appendHeader('Set-Cookie', (0, cookie_1.serializeCookie)(name, value, options));
        },
        delete(name) {
            if (shouldIgnoreCookie(ignoreNonServerSideCookies, name)) {
                return;
            }
            const encodedName = (0, cookie_1.ensureEncodedForJSCookie)(name);
            const setCookieValue = `${encodedName}=;Expires=${exports.DATE_IN_THE_PAST.toUTCString()}`;
            const existingValues = getExistingSetCookieValues(response.getHeader('Set-Cookie'));
            // if the value for cookie deletion is already in the Set-Cookie header, we
            // don't need to add the deletion value again.
            if (existingValues.includes(setCookieValue)) {
                return;
            }
            response.appendHeader('Set-Cookie', setCookieValue);
        },
    };
};
const createMutableCookieStoreFromHeaders = (headers, ignoreNonServerSideCookies) => {
    const setFunc = (name, value, options) => {
        if (shouldIgnoreCookie(ignoreNonServerSideCookies, name)) {
            return;
        }
        headers.append('Set-Cookie', (0, cookie_1.serializeCookie)(name, value, options));
    };
    const deleteFunc = name => {
        if (shouldIgnoreCookie(ignoreNonServerSideCookies, name)) {
            return;
        }
        headers.append('Set-Cookie', `${(0, cookie_1.ensureEncodedForJSCookie)(name)}=;Expires=${exports.DATE_IN_THE_PAST.toUTCString()}`);
    };
    return {
        set: setFunc,
        delete: deleteFunc,
    };
};
const getExistingSetCookieValues = (values) => values === undefined ? [] : Array.isArray(values) ? values : [String(values)];
const shouldIgnoreCookie = (ignoreNonServerSideCookies, cookieName) => ignoreNonServerSideCookies && !(0, utils_1.isServerSideAuthAllowedCookie)(cookieName);
